import React, { useEffect, useState, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as AlterIcon } from '../../assets/icons/alert-icon.svg';
import { ReactComponent as Brightness } from '../../assets/icons/brightness.svg';
import { ReactComponent as CloseCircle } from '../../assets/icons/close-circle.svg';
import { ReactComponent as Darkness } from '../../assets/icons/darkness.svg';
import { ReactComponent as Desktop } from '../../assets/icons/desktop.svg';
import { ReactComponent as Downarrow } from '../../assets/icons/down-arrow.svg';
import { ReactComponent as InfoIcon } from '../../assets/icons/information-icon.svg';
import { ReactComponent as Notification } from '../../assets/icons/notification.svg';
import { ReactComponent as Uparrow } from '../../assets/icons/up-arrow.svg';
import profile from '../../assets/images/profile.jpg';
import { brandImage } from '../../config/branding';
import ThemeContext from '../../contexts/theme-context/ThemeContext';
import { FilterFacilityTagInterFace } from '../../interface/facility-tag.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  changeSelectedCurrentFacilityTag,
  fetchCurrentFacilityTagsList,
} from '../../redux/slice/FacilityTagSlice';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { fetchSessionUser } from '../../redux/slice/UserManagementSlice';
import { getCookie, removeCookie } from '../../shared-functions/Cookies';
import { checkPermission, decodeJWT } from '../../shared-functions/JWTDocodeFunction';
import { removeLocalStorageItem } from '../../shared-functions/LocalStorage';
import auth from '../../utils/api/auth';
import notificationApi from '../../utils/api/notification';

interface NotificationInterface {
  createdAt?: string;
  createdBy?: string;
  message: string;
  status: string;
  title: string;
  type: string;
  updatedAt?: string;
  _id?: string;
}

/**
 * Header component
 */
function Header() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { sessionUser } = useAppSelector((state) => state.users);
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);

  const reference = useRef<HTMLDivElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownFacilityOpen, setDropdownFacilityOpen] = useState(false);
  const [dropdownNotification, setDropdownNotification] = useState(false);
  const [notifications, steNotification] = useState<NotificationInterface[]>([]);

  const [focused, setFocused] = useState<boolean>(true);
  const [visible, setVisible] = useState<boolean>(!document.hidden);
  const readNotificationsReference = useRef<string[]>([]);
  const [notificationCount, setNotificationCount] = useState<number>(0);

  /**
   * Fetch teh notification data function
   */
  const fetchData = () => {
    notificationApi
      .fetchNotification()
      .then((response) => {
        steNotification(
          response.data.map((notification: NotificationInterface) => ({
            ...notification,
            link: 'Learn More',
          })),
        );
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Open notification function
   */
  const openNotification = () => {
    setDropdownNotification(true);
    fetchData();
  };

  /**
   * Profile click functionality
   */
  const myProfile = () => {
    setDropdownOpen(false);
    navigate('./profile');
  };

  /**
   * alters
   */
  const alters = () => {
    navigate('./alerts/all');
    setDropdownOpen(false);
  };

  /**
   * On dropdown click
   */
  const dropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  /**
   * On dropdown facility click
   */
  const dropDownFacility = () => {
    setDropdownFacilityOpen(!dropdownFacilityOpen);
  };

  /**
   * Function to handle logout API and token deletion from cookies
   */
  const handleTokenDelete = () => {
    const accessToken = getCookie('ev-access-token');
    auth
      .logout({ accessToken })
      .then(() => {
        removeCookie('ev-refresh-token');
        removeCookie('ev-access-token');
        removeLocalStorageItem('accountVerificationStatus');
        navigate('../');
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const { theme, setTheme } = useContext(ThemeContext);
  /**
   * Theme change
   */
  const lightDarkness = (data: string) => {
    setTheme(data); // This will update the theme
    setDropdownOpen(false);
  };

  /**
   * roles
   */
  const handleClose = () => {
    setDropdownNotification(false);
  };

  const headerList1 = [
    {
      icon: Brightness,
      active: theme === 'light',
      key: 'light',
      /**
       * Theme change
       */
      function: () => lightDarkness('light'),
    },
    {
      icon: Darkness,
      active: theme === 'dark',
      key: 'dark',
      /**
       * Theme change
       */
      function: () => lightDarkness('dark'),
    },
    {
      icon: Desktop,
      active: theme === 'system-default',
      key: 'system-default',
      /**
       * Theme change
       */
      function: () => {},
    },
  ];

  const headerList2 = [
    {
      label: 'My Profile',
      addClass: '',
      function: myProfile,
      allow: checkPermission(['profile']),
    },
    {
      label: 'Alerts',
      addClass: '',
      function: alters,
      allow: checkPermission(['alerts']),
    },
    {
      label: 'Logout',
      addClass: 'text-danger',
      function: handleTokenDelete,
      allow: true,
    },
  ];

  /**
   * Get notification count
   */
  const fetchNotificationCount = () => {
    // Example API call
    notificationApi
      .fetchNotificationCount()
      .then((response) => {
        if (response?.data?.unreadNotificationCount !== undefined) {
          setNotificationCount(response.data?.unreadNotificationCount);
        }
      })
      .catch(() => {
        // Handle error if needed
      });
  };

  /**
   * Make API
   * @param entries
   */
  const fetchElementData = async () => {
    try {
      if (readNotificationsReference.current.length) {
        await notificationApi.updateNotificationAsRead({
          notificationIds: readNotificationsReference.current,
        });
        fetchNotificationCount();
        readNotificationsReference.current = [];
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Filter by facility
   */
  const filterByFacility = (filterData: FilterFacilityTagInterFace) => {
    dispatch(changeSelectedCurrentFacilityTag(filterData));
    setDropdownFacilityOpen(false);
  };

  useEffect(() => {
    dispatch(fetchCurrentFacilityTagsList());

    /**
     * Close popup
     */
    const handleClickOutside = (event: MouseEvent) => {
      if (reference.current && !reference.current.contains(event.target as Node)) {
        setDropdownOpen(false);
        setDropdownNotification(false);
        fetchElementData(); // Trigger API for the element in view
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    /**
     * on-Focus
     * @returns
     */
    const onFocus = () => setFocused(true);
    /**
     * on-Blur
     * @returns
     */
    const onBlur = () => setFocused(false);
    /**
     * on-visibility-change
     * @returns
     */
    const onVisibilityChange = () => setVisible(!document.hidden);

    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;

    /**
     * If a user is active fetch notification with an time interval
     */
    const startInterval = () => {
      fetchNotificationCount(); // Initial API call
      intervalId = setInterval(fetchNotificationCount, 5 * 60 * 1000); // Call every 5 minutes
    };

    /**
     * Function to clear the interval
     */
    const stopInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };

    if (focused && visible) {
      startInterval();
    } else {
      stopInterval();
    }

    return () => {
      stopInterval(); // Cleanup interval on component unmount
    };
  }, [focused, visible]);

  useEffect(() => {
    if (!sessionUser.id) {
      const accessToken = getCookie('ev-access-token');
      const decodedAccessToken = decodeJWT(accessToken || '');
      dispatch(fetchSessionUser(decodedAccessToken?.sub || '')); // sub -> userId in decoded token
    }
  }, []);

  // Code to update the status of the notification to read

  /* eslint-disable consistent-return */
  useEffect(() => {
    let visibleElements: string[] = [];
    /**
     * Intersection Observer callback
     */
    const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const elementId = entry.target.getAttribute('data-id');
          const elementStatus = entry.target.getAttribute('data-status');
          if (elementId && elementStatus === 'unread' && !visibleElements.includes(elementId)) {
            readNotificationsReference.current = [...readNotificationsReference.current, elementId];
            const updatedVisibleElement = [...visibleElements, elementId];
            visibleElements = updatedVisibleElement;
          }
        }
      });
    };

    // Only setup intersection observer once the modal is open
    if (dropdownNotification) {
      const observer = new IntersectionObserver(handleIntersection, {
        root: null, // Observe visibility in the viewport
        threshold: 0.5, // Trigger when 50% of the element is in view
      });

      const elements = document.querySelectorAll('.today-notification-content');
      elements.forEach((element) => observer.observe(element));

      return () => observer.disconnect(); // Cleanup observer on unmount
    }
  }, [dropdownNotification, notifications]);

  return (
    <div className="admin-header">
      <div className="admin-header-content">
        <div className="admin-header-left" />
        <div className="admin-header-center">
          <img
            src={theme === 'dark' ? brandImage.logoDarkTheme : brandImage.logo}
            alt="logo"
            className="header-image"
          />
        </div>
        <div className="admin-header-right">
          <div className="item">
            <div className="notification" onClick={openNotification} role="presentation">
              <Notification />
              {notificationCount > 0 && <div className="tag tag-danger">{notificationCount}</div>}
            </div>
            {dropdownNotification && (
              <div className="dropdown-notification" ref={reference}>
                <div className="dropdown-layout">
                  <div className="notification-header">
                    <div className="notification-title">Notification</div>
                    <div role="presentation" onClick={handleClose} className="dropdown-close">
                      <CloseCircle />
                    </div>
                  </div>
                  <div className="notification-list-content">
                    <div className="notification-content">
                      <div className="today-notification">
                        {/* <div className="alter-notification-title">{item.date}</div> */}
                        {notifications.map((list: any) => (
                          <div
                            className="today-notification-content"
                            key={list.title}
                            data-id={list._id}
                            data-status={list.status}>
                            <div className="notification-message-icon">
                              <span className="alter-icon">
                                {list.type === 'notification' ? <InfoIcon /> : <AlterIcon />}
                              </span>
                            </div>
                            <div className="notification-message">
                              <p>{list.title}</p>
                              <div className="main-notification-content">
                                <p>{list.message}</p>
                                {/* <p className="learn-more">{list.link}</p> */}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="item">
            <div className="profile">
              <div className="item profile-details" onClick={dropDownFacility} role="presentation">
                {currentFacilityTagsList.map((value) => {
                  return (
                    <div className="profile-name" role="presentation">
                      {value.isSelected && value.facilityTagName}
                    </div>
                  );
                })}
                <div className="profile-dropdown-icon">
                  {dropdownOpen ? <Uparrow /> : <Downarrow />}
                </div>
              </div>
              {dropdownFacilityOpen && (
                <div className="dropdown-content" ref={reference}>
                  <div className="dropdown-layout">
                    <div className="dropdown-facility-tag-body">
                      <div className="body-list">
                        {currentFacilityTagsList.map((value) => (
                          <div
                            className={`${value.isSelected ? 'text-success' : 'item'}`}
                            onClick={() =>
                              filterByFacility({
                                facilityTagName: value.facilityTagName,
                              })
                            }
                            role="presentation">
                            <div>{value.facilityTagName}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="item">
            <div className="profile">
              <div className="item profile-image">
                <img src={sessionUser?.profileImage?.signedUrl || profile} alt="profile" />
              </div>
              <div className="item profile-details" onClick={dropdown} role="presentation">
                <div className="profile-name">{sessionUser?.name}</div>
                <div className="profile-dropdown-icon">
                  {dropdownOpen ? <Uparrow /> : <Downarrow />}
                </div>
              </div>
              {dropdownOpen && (
                <div className="dropdown-content" ref={reference}>
                  <div className="dropdown-layout">
                    <div className="dropdown-header">
                      {headerList1.map((value: any) => (
                        <div
                          className={`header-item ${value.active && 'open'}`}
                          onClick={() => value.function(value.key)}
                          role="presentation">
                          <value.icon />
                        </div>
                      ))}
                    </div>
                    <div className="dropdown-body">
                      <div className="body-list">
                        {headerList2.map(
                          (value) =>
                            value.allow && (
                              <div className="item" onClick={value.function} role="presentation">
                                <div className={value.addClass}>{value.label}</div>
                                <div className="list-icon">
                                  <Downarrow />
                                </div>
                              </div>
                            ),
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Header;
